import { useMutation } from '@apollo/client'
import { LOGIN_VOLUNTEER_MANAGER } from '@ec/apollo/src/mutations/auth'
import { LoginResponseType } from '@ec/types'
import { Button, Checkbox, Input } from '@ec/ui'
import useErrorHandler from 'hooks/UseErrorHandler'
import { useEffect } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { login } from 'slices/auth'

type LoginProps = {
  email: string
  password: string
  remember_me: boolean
}

const LoginPage = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const errorHandler = useErrorHandler()

  const [loginMutation, { 
    data: loginResponse, 
    loading: isLoginLoading, 
    error: loginError,
  }] = useMutation<{ loginVolunteerManager: LoginResponseType }>(LOGIN_VOLUNTEER_MANAGER)

  const { register, handleSubmit, setError, formState: { errors } } = useForm<LoginProps>({
    defaultValues: {
      remember_me: false,
    },
  })

  const onSubmit: SubmitHandler<LoginProps> = (form) => {
    loginMutation({ 
      variables: { 
        input: {
          email: form.email, 
          password: form.password, 
        },
      }, 
    })
  }

  useEffect(() => {
    if (loginResponse?.loginVolunteerManager) {
      if (loginResponse.loginVolunteerManager.user) {
        dispatch(login(loginResponse.loginVolunteerManager))
        navigate('/')
      }
      // Commenting out TFA functionality for now
      if (loginResponse.loginVolunteerManager.user !== null && loginResponse.loginVolunteerManager.two_factor_challenged === false) {
        dispatch(login(loginResponse.loginVolunteerManager))
        navigate('/')
      } 
      else if (loginResponse.loginVolunteerManager.user === null && loginResponse.loginVolunteerManager.two_factor_challenged === true) {
        navigate('/login/challenge')
      }
    }
  }, [loginResponse])

  useEffect(() => {
    if (loginError) {
      errorHandler(loginError, setError)
    }
  }, [loginError])

  return (
    <>
      <div className="flex min-h-full flex-col justify-center py-12 sm:px-6 lg:px-8">

        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900">Sign in to your account</h2>
        </div>
 
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">

          <div className="bg-white py-8 px-4 sm:rounded-lg sm:px-10">

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              
              <Input 
                label="Email"
                {...register('email')} 
                type="email" 
                error={errors.email?.message}
                required
                expanding
              />

              <Input 
                label="Password"
                {...register('password')} 
                type="password" 
                error={errors.password?.message}
                required
              />

              <Checkbox label="Remember me" {...register('remember_me')} />
              
              <Button block isLoading={isLoginLoading}>
                Sign in
              </Button>

              <p className="text-sm text-center text-primary-blue">
                <Link to="/forgot-password">Forgotten your password?</Link>
              </p>

            </form>

          </div>

        </div>

      </div>
    </>
  )
}

export default LoginPage